import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button'; // Assuming shadcn Button

interface Props {
  children: ReactNode;
  fallbackMessage?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    // You could log this to an error reporting service here
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    // Attempt to reload or navigate to a safe page
    // window.location.reload(); // Or navigate('/')
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground p-4 text-center">
          <h1 className="text-2xl font-semibold text-destructive mb-4">
            Oops! Something went wrong.
          </h1>
          <p className="text-muted-foreground mb-6">
            {this.props.fallbackMessage || "We're sorry for the inconvenience. Please try refreshing the page, or contact support if the problem persists."}
          </p>
          {this.state.error && process.env.NODE_ENV === 'development' && (
            <details className="mb-4 p-2 border rounded bg-muted text-left text-xs w-full max-w-lg">
              <summary className="cursor-pointer font-medium">Error Details (Dev Mode)</summary>
              <pre className="mt-2 whitespace-pre-wrap break-all">
                {this.state.error.toString()}
                {this.state.error.stack && `\n\nStack Trace:\n${this.state.error.stack}`}
              </pre>
            </details>
          )}
          <Button onClick={this.handleReset} variant="outline">
            Try Again or Go Home
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
